'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

type Card = { id: string; front: string; back: string; known: boolean };
type Deck = { id: string; name: string; cards: Card[] };

const STORAGE_KEY = 'cleanor-flashcards-v1';

function createId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function isCard(value: unknown): value is Card {
  const c = value as Card;
  return (
    !!c &&
    typeof c.id === 'string' &&
    typeof c.front === 'string' &&
    typeof c.back === 'string' &&
    typeof c.known === 'boolean'
  );
}

function isDeck(value: unknown): value is Deck {
  const d = value as Deck;
  return (
    !!d &&
    typeof d.id === 'string' &&
    typeof d.name === 'string' &&
    Array.isArray(d.cards) &&
    d.cards.every(isCard)
  );
}

function starterDeck(): Deck {
  return {
    id: createId(),
    name: 'Sample deck',
    cards: [
      { id: createId(), front: 'Capital of France', back: 'Paris', known: false },
      { id: createId(), front: 'H2O is commonly called?', back: 'Water', known: false },
      { id: createId(), front: '7 x 8', back: '56', known: false },
    ],
  };
}

export default function BrowserFlashcards() {
  const [decks, setDecks] = useState<Deck[]>([]);
  const [activeDeckId, setActiveDeckId] = useState<string | null>(null);
  const [order, setOrder] = useState<string[]>([]);
  const [position, setPosition] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [front, setFront] = useState('');
  const [back, setBack] = useState('');
  const [newDeckName, setNewDeckName] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const skipFirstSave = useRef(true);
  const importInputRef = useRef<HTMLInputElement>(null);

  // Load persisted decks after mount so the server render stays stable.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let cancelled = false;
    async function load() {
      await Promise.resolve();
      if (cancelled) return;
      let loaded: Deck[] | null = null;
      try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw) as unknown;
          if (Array.isArray(parsed)) {
            loaded = parsed.filter(isDeck);
          }
        }
      } catch {
        // Ignore malformed storage.
      }
      const next = loaded && loaded.length > 0 ? loaded : [starterDeck()];
      setDecks(next);
      setActiveDeckId(next[0].id);
      setHydrated(true);
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Persist after hydration, skipping the initial run.
  useEffect(() => {
    if (!hydrated || typeof window === 'undefined') return;
    if (skipFirstSave.current) {
      skipFirstSave.current = false;
      return;
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(decks));
    } catch {
      // Ignore quota errors.
    }
  }, [decks, hydrated]);

  const activeDeck = useMemo(
    () => decks.find((deck) => deck.id === activeDeckId) ?? null,
    [decks, activeDeckId],
  );

  // Rebuild the study order whenever the active deck (or its cards) change.
  useEffect(() => {
    if (!activeDeck) {
      setOrder([]);
      return;
    }
    setOrder(activeDeck.cards.map((card) => card.id));
    setPosition(0);
    setFlipped(false);
  }, [activeDeck?.id, activeDeck?.cards.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const orderedCards = useMemo(() => {
    if (!activeDeck) return [];
    const byId = new Map(activeDeck.cards.map((card) => [card.id, card]));
    return order.map((id) => byId.get(id)).filter((card): card is Card => Boolean(card));
  }, [activeDeck, order]);

  const currentCard = orderedCards[position] ?? null;
  const knownCount = activeDeck ? activeDeck.cards.filter((card) => card.known).length : 0;
  const total = activeDeck ? activeDeck.cards.length : 0;

  function updateDeck(deckId: string, updater: (deck: Deck) => Deck) {
    setDecks((prev) => prev.map((deck) => (deck.id === deckId ? updater(deck) : deck)));
  }

  function addDeck() {
    const name = newDeckName.trim();
    if (!name) return;
    const deck: Deck = { id: createId(), name, cards: [] };
    setDecks((prev) => [...prev, deck]);
    setActiveDeckId(deck.id);
    setNewDeckName('');
  }

  function deleteDeck() {
    if (!activeDeck) return;
    const remaining = decks.filter((deck) => deck.id !== activeDeck.id);
    const next = remaining.length > 0 ? remaining : [starterDeck()];
    setDecks(next);
    setActiveDeckId(next[0].id);
  }

  function addCard() {
    if (!activeDeck) return;
    const f = front.trim();
    const b = back.trim();
    if (!f || !b) {
      setError('Both the front and back are required.');
      return;
    }
    setError(null);
    updateDeck(activeDeck.id, (deck) => ({
      ...deck,
      cards: [...deck.cards, { id: createId(), front: f, back: b, known: false }],
    }));
    setFront('');
    setBack('');
  }

  function deleteCard(cardId: string) {
    if (!activeDeck) return;
    updateDeck(activeDeck.id, (deck) => ({
      ...deck,
      cards: deck.cards.filter((card) => card.id !== cardId),
    }));
  }

  function markCurrent(known: boolean) {
    if (!activeDeck || !currentCard) return;
    updateDeck(activeDeck.id, (deck) => ({
      ...deck,
      cards: deck.cards.map((card) => (card.id === currentCard.id ? { ...card, known } : card)),
    }));
    goNext();
  }

  function goNext() {
    setFlipped(false);
    setPosition((prev) => (orderedCards.length === 0 ? 0 : (prev + 1) % orderedCards.length));
  }

  function goPrev() {
    setFlipped(false);
    setPosition((prev) =>
      orderedCards.length === 0 ? 0 : (prev - 1 + orderedCards.length) % orderedCards.length,
    );
  }

  function shuffle() {
    if (typeof window === 'undefined' || orderedCards.length < 2) return;
    setOrder((prev) => {
      const next = [...prev];
      for (let i = next.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [next[i], next[j]] = [next[j], next[i]];
      }
      return next;
    });
    setPosition(0);
    setFlipped(false);
  }

  function resetProgress() {
    if (!activeDeck) return;
    updateDeck(activeDeck.id, (deck) => ({
      ...deck,
      cards: deck.cards.map((card) => ({ ...card, known: false })),
    }));
  }

  function exportDeck() {
    if (typeof window === 'undefined' || !activeDeck) return;
    try {
      const blob = new Blob([JSON.stringify(activeDeck, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${activeDeck.name.replace(/[^\w-]+/g, '-') || 'deck'}.json`;
      document.body.append(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setMessage('Deck exported as JSON.');
    } catch {
      setError('The browser could not export the deck.');
    }
  }

  function importDeck(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result)) as unknown;
        const candidate = parsed as Partial<Deck>;
        if (!candidate || !Array.isArray(candidate.cards)) {
          throw new Error('bad shape');
        }
        const deck: Deck = {
          id: createId(),
          name:
            typeof candidate.name === 'string' && candidate.name ? candidate.name : 'Imported deck',
          cards: candidate.cards.filter(isCard).map((card) => ({ ...card, id: createId() })),
        };
        setDecks((prev) => [...prev, deck]);
        setActiveDeckId(deck.id);
        setError(null);
        setMessage(`Imported "${deck.name}" (${deck.cards.length} cards).`);
      } catch {
        setError('That file is not a valid flashcard deck JSON.');
      }
    };
    reader.readAsText(file);
  }

  return (
    <div className="tool-live-shell">
      <style>{`
        .flashcard-flip { perspective: 1000px; }
        .flashcard-inner {
          position: relative;
          width: 100%;
          min-height: 180px;
          transition: transform 0.5s;
          transform-style: preserve-3d;
          cursor: pointer;
        }
        .flashcard-inner.is-flipped { transform: rotateY(180deg); }
        .flashcard-face {
          position: absolute;
          inset: 0;
          -webkit-backface-visibility: hidden;
          backface-visibility: hidden;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 1.25rem;
          border-radius: 0.85rem;
          border: 1px solid rgba(148, 163, 184, 0.35);
          background: rgba(148, 163, 184, 0.12);
          font-size: 1.1rem;
          font-weight: 600;
          word-break: break-word;
        }
        .flashcard-face .flashcard-hint {
          margin-top: 0.6rem;
          font-size: 0.7rem;
          font-weight: 500;
          opacity: 0.6;
        }
        .flashcard-back { transform: rotateY(180deg); }
      `}</style>

      <div className="tool-live-header">
        <span className="tool-live-badge">Study</span>
        <h2 className="tool-live-title">Flashcards</h2>
        <p className="tool-live-subtitle">
          Build decks of front/back cards, flip to reveal, shuffle, and mark what you know. Your
          decks are saved in this browser and survive a reload. Export or import a deck as JSON any
          time.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Active deck</span>
              <select onChange={(e) => setActiveDeckId(e.target.value)} value={activeDeckId ?? ''}>
                {decks.map((deck) => (
                  <option key={deck.id} value={deck.id}>
                    {deck.name} ({deck.cards.length})
                  </option>
                ))}
              </select>
            </label>

            <label className="tool-live-field tool-live-field-full">
              <span>New deck name</span>
              <input
                onChange={(e) => setNewDeckName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addDeck();
                  }
                }}
                placeholder="e.g. Spanish verbs"
                type="text"
                value={newDeckName}
              />
            </label>
          </div>

          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={addDeck} type="button">
              Add deck
            </button>
            <button className="button button-secondary" onClick={deleteDeck} type="button">
              Delete deck
            </button>
            <button
              className="button button-secondary"
              disabled={!activeDeck}
              onClick={exportDeck}
              type="button"
            >
              Export JSON
            </button>
            <button
              className="button button-secondary"
              onClick={() => importInputRef.current?.click()}
              type="button"
            >
              Import JSON
            </button>
            <input
              accept="application/json,.json"
              onChange={importDeck}
              ref={importInputRef}
              style={{ display: 'none' }}
              type="file"
            />
          </div>

          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Card front</span>
              <input
                onChange={(e) => setFront(e.target.value)}
                placeholder="Question or prompt"
                type="text"
                value={front}
              />
            </label>
            <label className="tool-live-field">
              <span>Card back</span>
              <input
                onChange={(e) => setBack(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCard();
                  }
                }}
                placeholder="Answer"
                type="text"
                value={back}
              />
            </label>
          </div>
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={addCard} type="button">
              Add card
            </button>
          </div>

          {error ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {error}
            </p>
          ) : null}
          {message ? <p className="status-message status-success">{message}</p> : null}
          <p className="tool-live-panel-note">
            {total === 0
              ? 'This deck has no cards yet. Add one above.'
              : `${knownCount} of ${total} marked known. Everything stays on this device.`}
          </p>
        </section>

        <section className="tool-live-preview">
          {currentCard ? (
            <>
              <div className="flashcard-flip">
                <div
                  className={`flashcard-inner${flipped ? ' is-flipped' : ''}`}
                  onClick={() => setFlipped((v) => !v)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setFlipped((v) => !v);
                    }
                  }}
                >
                  <div className="flashcard-face">
                    <span>{currentCard.front}</span>
                    <span className="flashcard-hint">Tap to flip</span>
                  </div>
                  <div className="flashcard-face flashcard-back">
                    <span>{currentCard.back}</span>
                    <span className="flashcard-hint">Tap to flip back</span>
                  </div>
                </div>
              </div>

              <p className="tool-live-panel-note" style={{ textAlign: 'center' }}>
                Card {position + 1} of {orderedCards.length}
                {currentCard.known ? ' · known' : ''}
              </p>

              <div className="tool-live-actions">
                <button className="button button-secondary" onClick={goPrev} type="button">
                  Previous
                </button>
                <button className="button button-secondary" onClick={goNext} type="button">
                  Next
                </button>
                <button className="button button-secondary" onClick={shuffle} type="button">
                  Shuffle
                </button>
              </div>
              <div className="tool-live-actions">
                <button
                  className="button button-secondary"
                  onClick={() => markCurrent(true)}
                  type="button"
                >
                  Mark known
                </button>
                <button
                  className="button button-secondary"
                  onClick={() => markCurrent(false)}
                  type="button"
                >
                  Still learning
                </button>
                <button className="button button-secondary" onClick={resetProgress} type="button">
                  Reset progress
                </button>
              </div>
            </>
          ) : (
            <p className="tool-live-panel-note">Add a card to start studying.</p>
          )}

          {activeDeck && activeDeck.cards.length > 0 ? (
            <ul
              style={{
                listStyle: 'none',
                margin: '0.5rem 0 0',
                padding: 0,
                display: 'grid',
                gap: '0.4rem',
              }}
            >
              {activeDeck.cards.map((card) => (
                <li
                  key={card.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.45rem 0.65rem',
                    borderRadius: '0.5rem',
                    background: 'rgba(148, 163, 184, 0.1)',
                    fontSize: '0.85rem',
                  }}
                >
                  <span style={{ flex: 1, wordBreak: 'break-word' }}>
                    <strong>{card.front}</strong> → {card.back}
                    {card.known ? ' ✓' : ''}
                  </span>
                  <button
                    aria-label="Delete card"
                    className="button button-secondary"
                    onClick={() => deleteCard(card.id)}
                    style={{ padding: '0.2rem 0.5rem', flexShrink: 0 }}
                    type="button"
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      </div>
    </div>
  );
}
